import React from 'react';
import { fmtUsDate, fmtCompactNum } from './format';
import {
  ASK_EMPHASIS_PCT,
  contractLabel,
  daysToExpiry,
  fmtNetPremium,
  moneyness,
  premiumBarPct,
} from './topContracts';
import type { OptionType, TopContractRow } from './topContracts';

/**
 * One side of the Top Contracts board — calls or puts, ranked by net premium.
 *
 * ⚠️ **Renders the ranking it is given. It does not sort.** The server ranks;
 * re-sorting here would re-rank a list using only the rows that survived the
 * server's cut, which is a different board.
 */

/** Which ranking the rows arrived in. Drives the heading — see `SORT_NOTE`. */
export type SortMode = 'signed' | 'abs';

interface TopContractsPanelProps {
  optionType: OptionType;
  /** Ranked rows for this side, as served. */
  rows: TopContractRow[] | null;
  /**
   * Underlying price for the moneyness column.
   *
   * ⚠️ **A LIVE TICK, not the session's close.** On a board dated to a past
   * session the two can disagree, and the gap is not bounded by one overnight
   * move — holidays and halts widen it. The response carries `spot_as_of` if
   * that ever needs surfacing; the panel deliberately does not show it.
   */
  spot: number | null;
  /** `YYYY-MM-DD` — the session covered, shown beside the heading. */
  sessionDate?: string;
  /** `YYYY-MM-DD` — today, for the DTE column. */
  todayIso: string;
  /** Which ranking was requested. Defaults to the endpoint's own default. */
  sortMode?: SortMode;
  isLoading?: boolean;
  isError?: boolean;
  hasAccess: boolean;
  symbol: string;
  /** How many rows to list. Ten by default, matching the design. */
  limit?: number;
}

// ⚠️ Byte-identical to ContractLeaders' HEAD_CLASS / CELL_CLASS. These panels
// sit in the same column as the three leaderboards, so any divergence in
// alignment, padding or header weight reads as two different authors rather
// than as emphasis. Centred, like its neighbours — a right-aligned figure
// column is more scannable in isolation but not worth breaking the page over.
const HEAD_BASE = 'px-3 py-2 font-semibold text-gray-400 whitespace-nowrap text-center';
const CELL_BASE = 'px-3 py-1.5 whitespace-nowrap text-center';

/**
 * Net-premium bar tints, as literal rgba rather than theme classes.
 *
 * ⚠️ **Raw hex on purpose, and it must be kept in step with `@theme`.** The bar
 * is a gradient colour stop in an inline style, which Tailwind cannot supply —
 * a class only reaches `background-color`, and setting both leaves the class
 * silently overridden. These are `bull-blue` (#1C76A2) and `bear-red` (#D8483B)
 * from `src/index.css`. Tailwind v4 drops unknown tokens without erroring, so a
 * theme rename that misses these two leaves the bars on the old palette while
 * everything around them moves.
 */
const BAR_ALPHA = 0.28;
const BAR_CALL = `rgba(28, 118, 162, ${BAR_ALPHA})`;
const BAR_PUT = `rgba(216, 72, 59, ${BAR_ALPHA})`;

/**
 * ⚠️ **The guard QF asked us to hold.** Under `sort=abs` the top row of a panel
 * titled "Top Calls" can be the most net-SOLD call on the tape — contradicted
 * only by a minus sign, which readers take slower than they take position from
 * layout. QF proved it on the live board (a 1350C at −4.5M, 5.63% ask, ranked
 * first). The endpoint cannot warn us, so the heading has to.
 */
const SORT_NOTE: Record<SortMode, string | null> = {
  signed: null,
  abs: 'by size, both directions',
};

export function TopContractsPanel({
  optionType,
  rows,
  spot,
  sessionDate,
  todayIso,
  sortMode = 'signed',
  isLoading = false,
  isError = false,
  hasAccess,
  symbol,
  limit = 10,
}: TopContractsPanelProps): React.ReactElement {
  const isCall = optionType === 'Call';

  const listed = React.useMemo(() => (rows ?? []).slice(0, limit), [rows, limit]);

  const sideText = isCall ? 'text-bull-blue' : 'text-bear-red';
  const barTint = isCall ? BAR_CALL : BAR_PUT;
  const sortNote = SORT_NOTE[sortMode];

  return (
    // ⚠️ `p-4 bg-dark-surface` matches ContractLeaders and Overview's own
    // `Panel`, and the background is load-bearing rather than decorative:
    // SplitCard draws its dividers by showing border-coloured background
    // through a 1px grid gap, so a child without an opaque surface of its own
    // does not get a hairline — it gets the border colour behind the whole
    // panel. See the note on `Panel` in Overview.tsx.
    <section className="p-4 bg-dark-surface">
      <div className="flex items-baseline gap-2 mb-3 flex-wrap">
        {/* Side swatch. The only place the panel states which board this is
            without words, which matters when the two sit side by side. */}
        <span
          aria-hidden
          className={`inline-block w-2 h-2 rounded-[1px] translate-y-[-1px] ${
            isCall ? 'bg-bull-blue' : 'bg-bear-red'
          }`}
        />
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
          {/* Em dash, matching `GammaSection`'s "Net Gamma — Next Expiry" —
              same character and same colour. The dash is deliberately NOT
              muted: the Gamma tab passes one flat string, so its separator
              inherits the title's bone, and a grey one here would make the
              two tabs' headings read as different styles. */}
          Top {isCall ? 'Calls' : 'Puts'} — Net Premium
        </h2>

        {/* Dated from the data, never the wall clock — pre-open this ranking is
            the previous session's. */}
        {hasAccess && sessionDate && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-numeric">
            {fmtUsDate(sessionDate)}
          </span>
        )}

        {sortNote && (
          <span className="text-[10px] uppercase tracking-wider text-accent-yellow">
            {sortNote}
          </span>
        )}
      </div>

      {!hasAccess ? (
        <p className="text-gray-400 text-sm">Contract detail requires a Fuzion subscription.</p>
      ) : isError ? (
        <p className="text-bear-red text-sm">Failed to load contract data</p>
      ) : isLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-5 bg-dark-border rounded" />
          ))}
        </div>
      ) : !listed.length ? (
        <p className="text-gray-400 text-sm">
          No {isCall ? 'call' : 'put'} contracts for {symbol} this session
        </p>
      ) : (
        <>
          {/* ⚠️ Nine columns in half a page will scroll horizontally on narrower
              screens. `overflow-x-auto` keeps that scroll inside the panel
              rather than the page. */}
          <div className="overflow-x-auto rounded border border-dark-border">
            <table className="w-full text-xs">
              <thead className="bg-dark-surface-light border-b border-dark-border">
                <tr>
                  <th className={HEAD_BASE}>Contract</th>
                  <th className={HEAD_BASE}>Expiry</th>
                  <th className={HEAD_BASE}>DTE</th>
                  <th className={HEAD_BASE}>Net Prem</th>
                  <th className={HEAD_BASE}>Moneyness</th>
                  <th className={HEAD_BASE}>Volume</th>
                  <th className={HEAD_BASE}>OI</th>
                  <th className={HEAD_BASE}>% Ask</th>
                </tr>
              </thead>
              <tbody>
                {listed.map((row) => {
                  const dte = daysToExpiry(row.expiry, todayIso);
                  const money = spot == null ? null : moneyness(row.strike, spot, row.optionType);
                  const barPct = premiumBarPct(listed, row);
                  const loud = row.askPct >= ASK_EMPHASIS_PCT;

                  return (
                    <tr
                      key={`${row.expiry}-${row.strike}-${row.optionType}`}
                      className="border-b border-dark-border last:border-b-0 hover:bg-dark-surface-light transition-colors"
                    >
                      <td className={`${CELL_BASE} font-numeric font-bold text-white`}>
                        {contractLabel(row)}
                      </td>

                      {/* text-white to match ContractLeaders' expiry cell. */}
                      <td className={`${CELL_BASE} font-numeric text-white`}>
                        {fmtUsDate(row.expiry)}
                      </td>

                      <td className={`${CELL_BASE} font-numeric text-gray-400`}>
                        {dte == null ? '—' : `${dte}d`}
                      </td>

                      {/*
                        The bar is painted as a hard-stopped gradient on the CELL
                        rather than as a positioned element inside it. A `<td>`
                        does not reliably establish a containing block, so an
                        absolutely-positioned child lays out against the nearest
                        positioned ancestor and renders at inconsistent widths
                        across browsers. A background needs no containing block
                        and clips to the cell exactly.
                      */}
                      <td
                        className={`${CELL_BASE} font-numeric font-semibold ${sideText}`}
                        style={{
                          backgroundImage: `linear-gradient(to right, ${barTint} 0 ${barPct}%, transparent ${barPct}% 100%)`,
                        }}
                      >
                        {fmtNetPremium(row.netPremium)}
                      </td>

                      {/* ⚠️ Gold marks OUT of the money only — the tag AND the
                          percentage. ITM rows stay neutral: colouring both made
                          the column a wall of accent that distinguished nothing.

                          The percentage leads and the tag qualifies it, so the
                          number is what scans down the column.

                          ⚠️ ITM/OTM inverts between calls and puts — see
                          `moneyness`. The tag carries direction; the percentage
                          is unsigned. */}
                      <td
                        className={`${CELL_BASE} font-numeric ${
                          money && !money.itm ? 'text-accent-yellow' : 'text-gray-300'
                        }`}
                      >
                        {money == null ? (
                          '—'
                        ) : (
                          <>
                            {money.pct.toFixed(1)}%
                            <span
                              className={`ml-1.5 text-[9px] uppercase tracking-wider ${
                                money.itm ? 'text-gray-500' : 'text-accent-yellow'
                              }`}
                            >
                              {money.itm ? 'ITM' : 'OTM'}
                            </span>
                          </>
                        )}
                      </td>

                      <td className={`${CELL_BASE} font-numeric text-gray-300`}>
                        {fmtCompactNum(row.volume)}
                      </td>

                      <td className={`${CELL_BASE} font-numeric text-gray-400`}>
                        {fmtCompactNum(row.openInterest)}
                      </td>

                      <td className={CELL_BASE}>
                        <div className="flex items-center justify-center gap-2">
                          {/* Fixed-width track, so bar length compares down the
                              column. A percentage-width track would scale with
                              the cell and stop being comparable row to row. */}
                          <span className="relative inline-block w-10 h-1.5 rounded-sm bg-dark-bg overflow-hidden">
                            <span
                              className={`absolute inset-y-0 left-0 rounded-sm ${
                                loud ? 'bg-accent-yellow' : 'bg-gray-600'
                              }`}
                              style={{ width: `${Math.min(100, Math.max(0, row.askPct))}%` }}
                            />
                          </span>
                          <span
                            className={`font-numeric ${loud ? 'text-accent-yellow' : 'text-gray-400'}`}
                          >
                            {Math.round(row.askPct)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
